import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createIncomeSchedule,
  updateIncomeSchedule,
} from "../../lib/api-modules/income-schedules.js";
import {
  INCOME_SCHEDULE_TYPE_LABELS,
  PAYCHECK_FREQUENCY_LABELS,
  type CreateIncomeScheduleInput,
  type IncomeSchedule,
  type IncomeScheduleType,
  type PaycheckFrequency,
  type PaycheckPreTaxDeductions,
} from "../../lib/api-modules/types.js";
import { nextPayDates } from "../../lib/util/next-pay-dates.js";
import { formatCentsCurrency } from "../../lib/util/money.js";

/**
 * Recurring-paycheck schedule editor. Phase 12.
 *
 * Supports 3 schedule types:
 *   - salary_w2: full pay-stub model (gross + cadence + deductions +
 *     account routing + employer match).
 *   - contractor_1099: payer + estimated_tax_pct only (no cadence).
 *   - side_income: optional cadence + per-pay gross.
 *
 * The form auto-previews the next 4 paychecks via the pure-helper
 * `nextPayDates` mirror so the user sees their cadence before saving.
 */

export interface IncomeScheduleFormProps {
  readonly mode: "create" | "edit";
  readonly initial?: IncomeSchedule;
  readonly onClose: () => void;
}

interface FormState {
  schedule_type: IncomeScheduleType;
  payer: string;
  is_active: boolean;
  effective_from: string;
  end_date: string;
  // Compensation
  annual_gross_dollars: string; // input as dollars; converted to cents on submit
  per_pay_gross_dollars: string;
  // Cadence
  frequency: PaycheckFrequency | "";
  anchor_date: string;
  semi_monthly_day_1: string;
  semi_monthly_day_2: string;
  // Tax
  expected_withholding_pct: string;
  estimated_tax_pct: string;
  // Pre-tax deductions (each in dollars)
  k401k_traditional: string;
  roth_k401k: string;
  after_tax_k401k: string;
  hsa: string;
  medical_premium: string;
  dental_premium: string;
  vision_premium: string;
  fsa_healthcare: string;
  fsa_dependent_care: string;
  commuter: string;
  // Account routing
  net_pay_account_id: string;
  k401k_account_id: string;
  hsa_account_id: string;
  // Employer match
  k401k_employer_match_pct: string;
  k401k_employer_match_cap_pct: string;
  hsa_employer_per_pay: string;
  notes: string;
}

const todayIso = () => new Date().toISOString().slice(0, 10);

function dollarsToCents(s: string): number | undefined {
  if (!s.trim()) return undefined;
  const n = Number(s);
  if (!Number.isFinite(n)) return undefined;
  return Math.round(n * 100);
}

function centsToDollars(c?: number): string {
  if (c === undefined || c === null) return "";
  return (c / 100).toFixed(2);
}

function pctToString(n?: number): string {
  if (n === undefined || n === null) return "";
  return String(n);
}

function buildInitialState(s?: IncomeSchedule): FormState {
  const pre = s?.pre_tax_deductions_breakdown;
  return {
    schedule_type: s?.schedule_type ?? "salary_w2",
    payer: s?.payer ?? "",
    is_active: s?.is_active ?? true,
    effective_from: s?.effective_from ?? todayIso(),
    end_date: s?.end_date ?? "",
    annual_gross_dollars: centsToDollars(s?.annual_gross_cents),
    per_pay_gross_dollars: centsToDollars(s?.per_pay_gross_cents),
    frequency: s?.frequency ?? "",
    anchor_date: s?.anchor_date ?? "",
    semi_monthly_day_1: s?.semi_monthly_days?.[0]?.toString() ?? "",
    semi_monthly_day_2: s?.semi_monthly_days?.[1]?.toString() ?? "",
    expected_withholding_pct: pctToString(s?.expected_withholding_pct),
    estimated_tax_pct: pctToString(s?.estimated_tax_pct),
    k401k_traditional: centsToDollars(pre?.k401k_traditional_cents),
    roth_k401k: centsToDollars(pre?.roth_k401k_cents),
    after_tax_k401k: centsToDollars(pre?.after_tax_k401k_cents),
    hsa: centsToDollars(pre?.hsa_cents),
    medical_premium: centsToDollars(pre?.medical_premium_cents),
    dental_premium: centsToDollars(pre?.dental_premium_cents),
    vision_premium: centsToDollars(pre?.vision_premium_cents),
    fsa_healthcare: centsToDollars(pre?.fsa_healthcare_cents),
    fsa_dependent_care: centsToDollars(pre?.fsa_dependent_care_cents),
    commuter: centsToDollars(pre?.commuter_cents),
    net_pay_account_id: s?.net_pay_account_id ?? "",
    k401k_account_id: s?.k401k_account_id ?? "",
    hsa_account_id: s?.hsa_account_id ?? "",
    k401k_employer_match_pct: pctToString(s?.k401k_employer_match_pct),
    k401k_employer_match_cap_pct: pctToString(s?.k401k_employer_match_cap_pct),
    hsa_employer_per_pay: centsToDollars(
      s?.hsa_employer_contribution_per_pay_cents,
    ),
    notes: s?.notes ?? "",
  };
}

function buildPreTaxDeductions(
  f: FormState,
): PaycheckPreTaxDeductions | undefined {
  const fields: Array<[keyof PaycheckPreTaxDeductions, string]> = [
    ["k401k_traditional_cents", f.k401k_traditional],
    ["roth_k401k_cents", f.roth_k401k],
    ["after_tax_k401k_cents", f.after_tax_k401k],
    ["hsa_cents", f.hsa],
    ["medical_premium_cents", f.medical_premium],
    ["dental_premium_cents", f.dental_premium],
    ["vision_premium_cents", f.vision_premium],
    ["fsa_healthcare_cents", f.fsa_healthcare],
    ["fsa_dependent_care_cents", f.fsa_dependent_care],
    ["commuter_cents", f.commuter],
  ];
  const out: PaycheckPreTaxDeductions = {};
  let anySet = false;
  for (const [key, val] of fields) {
    const cents = dollarsToCents(val);
    if (cents !== undefined && cents > 0) {
      out[key] = cents;
      anySet = true;
    }
  }
  return anySet ? out : undefined;
}

function pctOrUndef(s: string): number | undefined {
  if (!s.trim()) return undefined;
  const n = Number(s);
  return Number.isFinite(n) ? n : undefined;
}

export function IncomeScheduleForm({
  mode,
  initial,
  onClose,
}: IncomeScheduleFormProps) {
  const [form, setForm] = useState<FormState>(buildInitialState(initial));
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
  };

  const previewDates = useMemo(() => {
    if (!form.frequency) return [];
    const through = new Date();
    through.setUTCDate(through.getUTCDate() + 60);
    const cadence: Parameters<typeof nextPayDates>[0] = {
      frequency: form.frequency,
      effective_from: form.effective_from,
    };
    if (form.anchor_date) cadence.anchor_date = form.anchor_date;
    if (form.semi_monthly_day_1 && form.semi_monthly_day_2) {
      cadence.semi_monthly_days = [
        Number(form.semi_monthly_day_1),
        Number(form.semi_monthly_day_2),
      ];
    }
    if (form.end_date) cadence.end_date = form.end_date;
    return nextPayDates(cadence, todayIso(), through.toISOString().slice(0, 10));
  }, [
    form.frequency,
    form.anchor_date,
    form.semi_monthly_day_1,
    form.semi_monthly_day_2,
    form.effective_from,
    form.end_date,
  ]);

  const grossPreview = useMemo(() => {
    const annual = dollarsToCents(form.annual_gross_dollars);
    const perPay = dollarsToCents(form.per_pay_gross_dollars);
    if (perPay !== undefined) return perPay;
    if (annual === undefined || !form.frequency) return undefined;
    const periods =
      form.frequency === "weekly"
        ? 52
        : form.frequency === "bi_weekly"
          ? 26
          : form.frequency === "semi_monthly"
            ? 24
            : form.frequency === "monthly"
              ? 12
              : form.frequency === "quarterly"
                ? 4
                : 1;
    return Math.round(annual / periods);
  }, [
    form.annual_gross_dollars,
    form.per_pay_gross_dollars,
    form.frequency,
  ]);

  const createM = useMutation({
    mutationFn: (input: CreateIncomeScheduleInput) => createIncomeSchedule(input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["income-schedules"] });
      onClose();
    },
    onError: (e) => setError((e).message),
  });
  const updateM = useMutation({
    mutationFn: (input: CreateIncomeScheduleInput) =>
      updateIncomeSchedule(initial!.schedule_id, input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["income-schedules"] });
      onClose();
    },
    onError: (e) => setError((e).message),
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const annualCents = dollarsToCents(form.annual_gross_dollars);
    const perPayCents = dollarsToCents(form.per_pay_gross_dollars);
    if (form.schedule_type === "salary_w2") {
      const hasAnnual = annualCents !== undefined;
      const hasPerPay = perPayCents !== undefined;
      if (hasAnnual === hasPerPay) {
        setError(
          "Salary (W-2) requires exactly one of annual gross or per-pay gross.",
        );
        return;
      }
      if (!form.frequency) {
        setError("Salary (W-2) requires a frequency.");
        return;
      }
      if (!form.anchor_date) {
        setError("Salary (W-2) requires an anchor date (first paycheck).");
        return;
      }
      if (
        form.frequency === "semi_monthly" &&
        (!form.semi_monthly_day_1 || !form.semi_monthly_day_2)
      ) {
        setError(
          "Semi-monthly schedules require both pay days (e.g. 15 and 31).",
        );
        return;
      }
    }
    if (
      form.schedule_type === "contractor_1099" &&
      !form.estimated_tax_pct
    ) {
      setError(
        "Contractor (1099) requires an estimated tax % (suggested setaside on each invoice).",
      );
      return;
    }

    const payload: CreateIncomeScheduleInput = {
      schedule_type: form.schedule_type,
      payer: form.payer,
      is_active: form.is_active,
      effective_from: form.effective_from,
    };
    if (form.end_date) payload.end_date = form.end_date;
    if (annualCents !== undefined) payload.annual_gross_cents = annualCents;
    if (perPayCents !== undefined) payload.per_pay_gross_cents = perPayCents;
    if (form.frequency) payload.frequency = form.frequency;
    if (form.anchor_date) payload.anchor_date = form.anchor_date;
    if (
      form.frequency === "semi_monthly" &&
      form.semi_monthly_day_1 &&
      form.semi_monthly_day_2
    ) {
      payload.semi_monthly_days = [
        Number(form.semi_monthly_day_1),
        Number(form.semi_monthly_day_2),
      ];
    }
    const wh = pctOrUndef(form.expected_withholding_pct);
    if (wh !== undefined) payload.expected_withholding_pct = wh;
    const et = pctOrUndef(form.estimated_tax_pct);
    if (et !== undefined) payload.estimated_tax_pct = et;
    const pre = buildPreTaxDeductions(form);
    if (pre) payload.pre_tax_deductions_breakdown = pre;
    if (form.net_pay_account_id)
      payload.net_pay_account_id = form.net_pay_account_id;
    if (form.k401k_account_id) payload.k401k_account_id = form.k401k_account_id;
    if (form.hsa_account_id) payload.hsa_account_id = form.hsa_account_id;
    const matchPct = pctOrUndef(form.k401k_employer_match_pct);
    if (matchPct !== undefined) payload.k401k_employer_match_pct = matchPct;
    const matchCap = pctOrUndef(form.k401k_employer_match_cap_pct);
    if (matchCap !== undefined) payload.k401k_employer_match_cap_pct = matchCap;
    const hsaEmp = dollarsToCents(form.hsa_employer_per_pay);
    if (hsaEmp !== undefined && hsaEmp > 0)
      payload.hsa_employer_contribution_per_pay_cents = hsaEmp;
    if (form.notes) payload.notes = form.notes;

    if (mode === "create") createM.mutate(payload);
    else updateM.mutate(payload);
  };

  const showSalaryFields = form.schedule_type === "salary_w2";
  const showContractorFields = form.schedule_type === "contractor_1099";
  const showDeductions =
    form.schedule_type === "salary_w2" ||
    form.schedule_type === "side_income";

  return (
    <form
      onSubmit={onSubmit}
      className="space-y-4 rounded-md border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-950"
    >
      <h3 className="text-sm font-semibold">
        {mode === "create" ? "Add schedule" : "Edit schedule"}
      </h3>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Labelled label="Type">
          <select
            value={form.schedule_type}
            onChange={(e) =>
              set("schedule_type", e.target.value as IncomeScheduleType)
            }
            className={inputCx}
          >
            {(
              Object.keys(
                INCOME_SCHEDULE_TYPE_LABELS,
              ) as IncomeScheduleType[]
            ).map((t) => (
              <option key={t} value={t}>
                {INCOME_SCHEDULE_TYPE_LABELS[t]}
              </option>
            ))}
          </select>
        </Labelled>
        <Labelled label="Payer (employer / client)">
          <input
            type="text"
            value={form.payer}
            onChange={(e) => set("payer", e.target.value)}
            required
            className={inputCx}
            placeholder="e.g. Acme Inc"
          />
        </Labelled>
        <Labelled label="Effective from">
          <input
            type="date"
            value={form.effective_from}
            onChange={(e) => set("effective_from", e.target.value)}
            required
            className={inputCx}
          />
        </Labelled>
        <Labelled label="End date (optional)">
          <input
            type="date"
            value={form.end_date}
            onChange={(e) => set("end_date", e.target.value)}
            className={inputCx}
          />
        </Labelled>
      </div>

      {showSalaryFields && (
        <>
          <h4 className="mt-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">
            Compensation
          </h4>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Labelled label="Annual gross ($)">
              <input
                type="number"
                inputMode="decimal"
                step="0.01"
                value={form.annual_gross_dollars}
                onChange={(e) => set("annual_gross_dollars", e.target.value)}
                className={inputCx}
                placeholder="200000"
                disabled={form.per_pay_gross_dollars.trim() !== ""}
              />
            </Labelled>
            <Labelled label="OR per-pay gross ($)">
              <input
                type="number"
                inputMode="decimal"
                step="0.01"
                value={form.per_pay_gross_dollars}
                onChange={(e) => set("per_pay_gross_dollars", e.target.value)}
                className={inputCx}
                placeholder="3846.15"
                disabled={form.annual_gross_dollars.trim() !== ""}
              />
            </Labelled>
            <Labelled label="Frequency">
              <select
                value={form.frequency}
                onChange={(e) =>
                  set("frequency", e.target.value as PaycheckFrequency | "")
                }
                className={inputCx}
              >
                <option value="">Select…</option>
                {(
                  Object.keys(
                    PAYCHECK_FREQUENCY_LABELS,
                  ) as PaycheckFrequency[]
                ).map((f) => (
                  <option key={f} value={f}>
                    {PAYCHECK_FREQUENCY_LABELS[f]}
                  </option>
                ))}
              </select>
            </Labelled>
            <Labelled label="Anchor date (first paycheck)">
              <input
                type="date"
                value={form.anchor_date}
                onChange={(e) => set("anchor_date", e.target.value)}
                className={inputCx}
              />
            </Labelled>
            {form.frequency === "semi_monthly" && (
              <>
                <Labelled label="Pay day 1 (1–31; 31 clamps to last day)">
                  <input
                    type="number"
                    min={1}
                    max={31}
                    value={form.semi_monthly_day_1}
                    onChange={(e) =>
                      set("semi_monthly_day_1", e.target.value)
                    }
                    className={inputCx}
                    placeholder="15"
                  />
                </Labelled>
                <Labelled label="Pay day 2">
                  <input
                    type="number"
                    min={1}
                    max={31}
                    value={form.semi_monthly_day_2}
                    onChange={(e) =>
                      set("semi_monthly_day_2", e.target.value)
                    }
                    className={inputCx}
                    placeholder="31"
                  />
                </Labelled>
              </>
            )}
            <Labelled label="Withholding % (federal + state + FICA blended)">
              <input
                type="number"
                inputMode="decimal"
                step="0.1"
                min={0}
                max={60}
                value={form.expected_withholding_pct}
                onChange={(e) =>
                  set("expected_withholding_pct", e.target.value)
                }
                className={inputCx}
                placeholder="24"
              />
            </Labelled>
          </div>

          {previewDates.length > 0 && (
            <div className="rounded-md bg-zinc-50 p-3 text-xs dark:bg-zinc-900/40">
              <div className="font-semibold">
                Cadence preview — next {Math.min(previewDates.length, 4)}{" "}
                paychecks
              </div>
              <ul className="mt-1 grid grid-cols-2 gap-1 sm:grid-cols-4">
                {previewDates.slice(0, 4).map((d) => (
                  <li key={d} className="font-mono">
                    {d}
                  </li>
                ))}
              </ul>
              {grossPreview !== undefined && (
                <div className="mt-2">
                  Per-paycheck gross:{" "}
                  <span className="font-medium">
                    {formatCentsCurrency(grossPreview)}
                  </span>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {showContractorFields && (
        <>
          <h4 className="mt-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">
            Contractor tax setaside
          </h4>
          <p className="text-xs text-zinc-500">
            No automatic paycheck materialization — invoice income is
            irregular. Hearth uses estimated_tax_pct to suggest a setaside
            on each manually-entered income event so quarterly taxes
            don't surprise you.
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Labelled label="Estimated tax % per invoice (federal + state combined)">
              <input
                type="number"
                inputMode="decimal"
                step="0.1"
                min={0}
                max={60}
                value={form.estimated_tax_pct}
                onChange={(e) => set("estimated_tax_pct", e.target.value)}
                required
                className={inputCx}
                placeholder="25"
              />
            </Labelled>
          </div>
        </>
      )}

      {showDeductions && (
        <>
          <h4 className="mt-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">
            Per-paycheck deductions ($)
          </h4>
          <p className="text-xs text-zinc-500">
            Capture the deductions that come off each paycheck. Roth-401k
            sits here even though it's post-tax for federal income — it's
            still deducted from gross before net pay. 401k + HSA require
            account routing below to materialize transaction rows.
          </p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <Labelled label="401k traditional">
              <input
                type="number"
                inputMode="decimal"
                step="0.01"
                value={form.k401k_traditional}
                onChange={(e) => set("k401k_traditional", e.target.value)}
                className={inputCx}
              />
            </Labelled>
            <Labelled label="Roth 401k">
              <input
                type="number"
                inputMode="decimal"
                step="0.01"
                value={form.roth_k401k}
                onChange={(e) => set("roth_k401k", e.target.value)}
                className={inputCx}
              />
            </Labelled>
            <Labelled label="After-tax 401k (mega-backdoor)">
              <input
                type="number"
                inputMode="decimal"
                step="0.01"
                value={form.after_tax_k401k}
                onChange={(e) => set("after_tax_k401k", e.target.value)}
                className={inputCx}
              />
            </Labelled>
            <Labelled label="HSA">
              <input
                type="number"
                inputMode="decimal"
                step="0.01"
                value={form.hsa}
                onChange={(e) => set("hsa", e.target.value)}
                className={inputCx}
              />
            </Labelled>
            <Labelled label="Medical premium">
              <input
                type="number"
                inputMode="decimal"
                step="0.01"
                value={form.medical_premium}
                onChange={(e) => set("medical_premium", e.target.value)}
                className={inputCx}
              />
            </Labelled>
            <Labelled label="Dental premium">
              <input
                type="number"
                inputMode="decimal"
                step="0.01"
                value={form.dental_premium}
                onChange={(e) => set("dental_premium", e.target.value)}
                className={inputCx}
              />
            </Labelled>
            <Labelled label="Vision premium">
              <input
                type="number"
                inputMode="decimal"
                step="0.01"
                value={form.vision_premium}
                onChange={(e) => set("vision_premium", e.target.value)}
                className={inputCx}
              />
            </Labelled>
            <Labelled label="FSA (healthcare)">
              <input
                type="number"
                inputMode="decimal"
                step="0.01"
                value={form.fsa_healthcare}
                onChange={(e) => set("fsa_healthcare", e.target.value)}
                className={inputCx}
              />
            </Labelled>
            <Labelled label="FSA (dependent care)">
              <input
                type="number"
                inputMode="decimal"
                step="0.01"
                value={form.fsa_dependent_care}
                onChange={(e) =>
                  set("fsa_dependent_care", e.target.value)
                }
                className={inputCx}
              />
            </Labelled>
            <Labelled label="Commuter benefits">
              <input
                type="number"
                inputMode="decimal"
                step="0.01"
                value={form.commuter}
                onChange={(e) => set("commuter", e.target.value)}
                className={inputCx}
              />
            </Labelled>
          </div>
          <h4 className="mt-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">
            Account routing (optional; required for 401k/HSA materialization)
          </h4>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <Labelled label="Net pay account ID">
              <input
                type="text"
                value={form.net_pay_account_id}
                onChange={(e) => set("net_pay_account_id", e.target.value)}
                className={inputCx}
                placeholder="(checking ULID)"
              />
            </Labelled>
            <Labelled label="401k account ID">
              <input
                type="text"
                value={form.k401k_account_id}
                onChange={(e) => set("k401k_account_id", e.target.value)}
                className={inputCx}
                placeholder="(401k ULID)"
              />
            </Labelled>
            <Labelled label="HSA account ID">
              <input
                type="text"
                value={form.hsa_account_id}
                onChange={(e) => set("hsa_account_id", e.target.value)}
                className={inputCx}
                placeholder="(HSA ULID)"
              />
            </Labelled>
          </div>
          <h4 className="mt-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">
            Employer match (optional)
          </h4>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <Labelled label="401k match % (e.g. 100 = 100% match)">
              <input
                type="number"
                inputMode="decimal"
                step="1"
                value={form.k401k_employer_match_pct}
                onChange={(e) =>
                  set("k401k_employer_match_pct", e.target.value)
                }
                className={inputCx}
              />
            </Labelled>
            <Labelled label="401k cap % of salary (e.g. 6)">
              <input
                type="number"
                inputMode="decimal"
                step="0.5"
                value={form.k401k_employer_match_cap_pct}
                onChange={(e) =>
                  set("k401k_employer_match_cap_pct", e.target.value)
                }
                className={inputCx}
              />
            </Labelled>
            <Labelled label="HSA employer / paycheck ($)">
              <input
                type="number"
                inputMode="decimal"
                step="0.01"
                value={form.hsa_employer_per_pay}
                onChange={(e) => set("hsa_employer_per_pay", e.target.value)}
                className={inputCx}
              />
            </Labelled>
          </div>
        </>
      )}

      <Labelled label="Notes (optional)">
        <textarea
          value={form.notes}
          onChange={(e) => set("notes", e.target.value)}
          rows={2}
          className={inputCx}
        />
      </Labelled>

      {error && (
        <p className="rounded-md bg-red-50 p-2 text-xs text-red-700 dark:bg-red-950/40 dark:text-red-300">
          {error}
        </p>
      )}

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="rounded-md px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-800"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={createM.isPending || updateM.isPending}
          className="rounded-md bg-brand-500 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-brand-600 disabled:opacity-60"
        >
          {mode === "create" ? "Create schedule" : "Save changes"}
        </button>
      </div>
    </form>
  );
}

const inputCx =
  "w-full rounded-md border border-zinc-300 bg-white px-2 py-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-950";

function Labelled({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <div className="mb-1 text-xs font-medium text-zinc-600 dark:text-zinc-400">
        {label}
      </div>
      {children}
    </label>
  );
}
